// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type { CustomerReservationDetails } from '@iheartjane/data-access';
import { Box, Hide, Link, Typography } from '@iheartjane/reefer';
import type { ReservationMode } from '@iheartjane/types';
import { titleCase } from '@iheartjane/utils';
import { useState } from 'react';

import { formatTime } from '@jane/shared/util';

const StyledGrid = styled(Box)(({ gap = 4 }: { gap?: number }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap,
  width: '100%',
  justifyItems: 'stretch',
  justifyContent: 'flex-end',
}));

const getReservationMode = (mode: ReservationMode) => {
  const isDelivery = mode === 'delivery';
  const isPickup = !isDelivery;

  return {
    isDelivery,
    isPickup,
  };
};

export const StoreSchedule = ({
  reservation,
}: {
  reservation: CustomerReservationDetails;
}) => {
  const [showSchedule, setShowSchedule] = useState(false);
  const { isDelivery, isPickup } = getReservationMode(
    reservation.reservation_mode
  );

  return (
    <>
      <Link
        onClick={() => {
          showSchedule ? setShowSchedule(false) : setShowSchedule(true);
        }}
      >
        {showSchedule
          ? 'Hide'
          : `View ${
              isDelivery
                ? 'delivery'
                : reservation.curbside_pickup_selected
                  ? 'curbside'
                  : 'pickup'
            }`}{' '}
        schedule
      </Link>
      <Hide isHidden={!showSchedule}>
        <MenuSchedule
          reservation={reservation}
          isDelivery={isDelivery}
          isPickup={isPickup}
        />
      </Hide>
    </>
  );
};

const MenuSchedule = ({
  reservation,
  isDelivery,
  isPickup,
}: {
  isDelivery: boolean;
  isPickup: boolean;
  reservation: CustomerReservationDetails;
}) => {
  let workingDays = reservation.store.working_hours;
  if (isDelivery && reservation.store.delivery_hours) {
    workingDays = reservation.store.delivery_hours;
  } else if (
    isPickup &&
    reservation.curbside_pickup_selected &&
    reservation.store.curbside_hours
  ) {
    workingDays = reservation.store.curbside_hours;
  } else if (isPickup && reservation.store.pickup_hours) {
    workingDays = reservation.store.pickup_hours;
  }

  const schedule = workingDays.map((schedule, index) => {
    const { day, period } = schedule;
    const startTime =
      period?.from &&
      formatTime({
        time: period.from,
        outputFormat: 'h:mm aaa',
        inputFormat: 'H:mm',
      });
    const endTime =
      period?.to &&
      formatTime({
        time: period.to,
        outputFormat: 'h:mm aaa',
        inputFormat: 'H:mm',
      });
    return (
      <StyledGrid key={day} pt={index !== 0 ? 4 : 0}>
        <Typography>{titleCase(day)}</Typography>
        {!!startTime && !!endTime ? (
          <Typography color="text-light" whiteSpace="nowrap">
            {startTime} - {endTime}
          </Typography>
        ) : (
          <Typography color="text-light">Closed</Typography>
        )}
      </StyledGrid>
    );
  });
  return <Box pt={8}>{schedule}</Box>;
};
